/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbLocalToolbar, AbLocalToolbarGroup } from "abreact/incubator/ui/toolbar/AbLocalToolbar";
import { $isPersonNode } from "abreact/input/markdown/nodes/PersonNode";
import { AbButton } from "abreact/misc/AbButton";
import { AbSize } from "abreact/misc/AbSize";
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isNodeSelection, CLICK_COMMAND, COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import React from "react";
function replacePersonWithText(editor) {
  editor.update(() => {
    const selection = $getSelection();
    const nodes = selection?.getNodes();
    if (nodes?.length === 1 && $isPersonNode(nodes[0])) {
      const person = nodes[0];
      const text = person.getDisplayName();
      const newText = $createTextNode(text);
      if (person.getParent() === $getRoot()) {
        // can't insert text into the root, so wrap it with a paragraph.
        const newPara = $createParagraphNode().append(newText);
        person.insertBefore(newPara);
      } else {
        person.insertBefore(newText);
      }
      person.remove();
    }
  });
}
export const PersonComponent = ({
  nodeKey,
  children
}) => {
  const abPersonComponentRef = React.useRef(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  // current Selection
  const [selection, setSelection] = React.useState(null);
  const [editor] = useLexicalComposerContext();
  const isEditable = editor.isEditable();
  const [popupElem, setPopupElem] = React.useState(undefined);
  const onClick = React.useCallback(payload => {
    const event = payload;
    let walker = event.target;
    let foundPerson = false;
    // walk up the dom looking for the current abPersonComponentRef.
    while (walker !== null) {
      if (walker === abPersonComponentRef.current) {
        foundPerson = true;
        break;
      }
      // eslint-disable-next-line abinitio/no-instanceof
      if (walker instanceof Element) walker = walker.parentElement;else walker = null;
    }
    if (foundPerson) {
      if (abPersonComponentRef.current) setPopupElem(abPersonComponentRef.current);
      if (event.shiftKey) {
        setSelected(!isSelected);
      } else {
        clearSelection();
        setSelected(true);
      }
      // Tell the other listeners that we've selected the person.
      editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
      return true;
    }
    return false; // click was not on a person, so let it propagate
  }, [isSelected, setSelected, clearSelection, editor]);
  React.useEffect(() => {
    return editor.registerUpdateListener(() => {
      if (isSelected && abPersonComponentRef.current) {
        setPopupElem(abPersonComponentRef.current);
      }
    });
  }, [editor, isSelected]);
  React.useEffect(() => {
    return mergeRegister(editor.registerUpdateListener(({
      editorState
    }) => {
      setSelection(editorState.read(() => $getSelection()));
    }), editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW));
  }, [editor, onClick]);
  return /*#__PURE__*/React.createElement("div", {
    ref: abPersonComponentRef
  }, children, isEditable && isSelected && $isNodeSelection(selection) && popupElem !== undefined && /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-markdown-popup-toolbar",
    origin: popupElem,
    positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
    overlay: false,
    flushContent: true,
    onClose: () => {
      //setPopupElem(undefined);
    },
    onBackgroundCaptureMouseDown: () => {
      //setPopupElem(undefined);
    }
  }, /*#__PURE__*/React.createElement(AbLocalToolbar, {
    type: "stand-alone"
  }, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, /*#__PURE__*/React.createElement(AbButton, {
    onClick: () => replacePersonWithText(editor),
    label: AbReactResources.markdownEditor_deletePerson(),
    size: AbSize.SMALL
  })))));
};
//# sourceMappingURL=PersonComponent.js.map
